import React, { useState, useEffect } from 'react';
import api from '../api/client';
import './Careers.css';

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState('전체');

  useEffect(() => {
    loadJobs();
  }, []);

  async function loadJobs() {
    try {
      setLoading(true);
      const data = await api.get('/jobs');
      setJobs(data);
      setError(null);
    } catch (err) {
      setError('채용공고를 불러오는 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const types = ['전체', ...new Set(jobs.map(j => j.type))];
  const filtered = filterType === '전체' ? jobs : jobs.filter(j => j.type === filterType);

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <h1>채용정보</h1>
          <p>허브와 함께 새로운 기회를 찾아보세요</p>
          <div className="breadcrumb">
            <span>HOME</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            <span className="current">채용정보</span>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {selected ? (
            <JobDetail job={selected} onBack={() => setSelected(null)} />
          ) : (
            <>
              {loading && <div className="state-message">채용공고를 불러오는 중입니다...</div>}

              {!loading && error && (
                <div className="state-message state-message--error">
                  {error}
                  <button className="btn btn-outline-navy" style={{ marginTop: '1rem' }} onClick={loadJobs}>다시 시도</button>
                </div>
              )}

              {!loading && !error && (
                <>
                  <div className="careers-header">
                    <div>
                      <h2>진행 중인 채용 공고</h2>
                      <p className="text-muted">총 <strong>{filtered.length}건</strong>의 공고가 있습니다.</p>
                    </div>
                    <div className="type-filter">
                      {types.map(t => (
                        <button
                          key={t}
                          className={`type-filter__btn ${filterType === t ? 'type-filter__btn--active' : ''}`}
                          onClick={() => setFilterType(t)}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="state-message">현재 조건에 맞는 채용공고가 없습니다.</div>
                  ) : (
                    <div className="job-list">
                      {filtered.map(job => (
                        <div key={job.id} className="job-card" onClick={() => setSelected(job)}>
                          <div className="job-card__left">
                            <div className="job-card__badges">
                              <span className="badge badge-blue">{job.type}</span>
                              {job.isNew && <span className="badge badge-accent">NEW</span>}
                              {job.isUrgent && <span className="badge badge-red">긴급</span>}
                            </div>
                            <h3 className="job-card__title">{job.title}</h3>
                            <div className="job-card__meta">
                              <span>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                {job.location}
                              </span>
                              <span>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none"><text x="12" y="18" textAnchor="middle" fontSize="17" fontWeight="700">₩</text></svg>
                                {job.salary}
                              </span>
                              <span>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                ~{job.deadline}
                              </span>
                            </div>
                            <div className="job-card__tags">
                              {(job.tags || []).map((tag, i) => <span key={i} className="tag">#{tag}</span>)}
                            </div>
                          </div>
                          <button className="btn btn-primary job-card__apply">지원하기</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}

/* ── Job Detail + Application ── */
function JobDetail({ job, onBack }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', career: '', message: '' });
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      alert('성함, 연락처, 이메일을 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('jobId', job.id);
      formData.append('jobTitle', job.title);
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      if (file) formData.append('resume', file);

      await api.postForm('/applications', formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || '지원서 제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button className="back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        공고 목록으로
      </button>

      <div className="job-detail">
        <div className="job-detail__header">
          <div>
            <div className="job-card__badges" style={{ marginBottom: '0.75rem' }}>
              <span className="badge badge-blue">{job.type}</span>
              {job.isNew && <span className="badge badge-accent">NEW</span>}
              {job.isUrgent && <span className="badge badge-red">긴급</span>}
            </div>
            <h2>{job.title}</h2>
            <p className="text-muted" style={{ marginTop: '0.5rem' }}>{job.company}</p>
          </div>
        </div>

        <div className="job-detail__body">
          <div className="job-detail__info">
            <div className="job-info-grid">
              {[
                { label: '근무지', value: job.location },
                { label: '고용형태', value: job.type },
                { label: '급여', value: job.salary },
                { label: '지원마감', value: job.deadline },
              ].map(item => (
                <div key={item.label} className="job-info-item">
                  <span className="job-info-item__label">{item.label}</span>
                  <span className="job-info-item__value">{item.value}</span>
                </div>
              ))}
            </div>

            <h4>업무 내용</h4>
            <p>{job.description}</p>

            <h4>지원 자격</h4>
            <ul className="req-list">
              {(job.requirements || []).map((r, i) => (
                <li key={i}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  {r}
                </li>
              ))}
            </ul>

            <div className="job-tags-section">
              {(job.tags || []).map((tag, i) => <span key={i} className="tag">{tag}</span>)}
            </div>
          </div>

          <div className="job-detail__apply">
            <h3>온라인 입사지원</h3>
            {submitted ? (
              <div className="apply-success">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <h4>지원이 완료되었습니다!</h4>
                <p>담당자가 검토 후 3 영업일 이내 연락 드리겠습니다.</p>
                <button className="btn btn-outline-navy" onClick={onBack}>공고 목록으로</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && <div className="form-error">{error}</div>}
                <div className="form-group">
                  <label>성함 <span className="required">*</span></label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="홍길동" />
                </div>
                <div className="form-group">
                  <label>연락처 <span className="required">*</span></label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="010-0000-0000" />
                </div>
                <div className="form-group">
                  <label>이메일 <span className="required">*</span></label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="example@email.com" />
                </div>
                <div className="form-group">
                  <label>경력 사항</label>
                  <input name="career" value={form.career} onChange={handleChange} placeholder="예: 3년 (전 직장명)" />
                </div>
                <div className="form-group">
                  <label>자기소개 / 지원 동기</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="간단히 작성해 주세요." />
                </div>
                <div className="form-group">
                  <label>이력서 첨부 <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>(PDF, Word, 최대 10MB)</span></label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={e => setFile(e.target.files[0] || null)}
                  />
                </div>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={submitting}>
                  {submitting ? '제출 중...' : '지원서 제출하기'}
                </button>
                <p style={{ fontSize: '0.78rem', color: 'var(--gray-500)', textAlign: 'center', marginTop: '0.75rem' }}>
                  제출한 정보는 채용 목적으로만 사용됩니다.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
